// Component: BacktestTradeLog.jsx
// Purpose: Trade log table with flat and hierarchical drilldown (Year > Quarter > Month > Day) views
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronRight, FiChevronDown, FiLayers } from 'react-icons/fi';
import { getDatePartsInTimezone } from '../../../../../utils/timezone';
import { readChargeComponents } from '../../../../../utils/backtestTrades';

// --> Format timestamp in exchange timezone for consistent display
const fmtExTz = (isoStr, tz) => {
    if (!isoStr) return '-';
    try {
        return new Intl.DateTimeFormat('en-IN', {
            timeZone: tz, year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(new Date(isoStr));
    } catch { return isoStr; }
};

const asNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const fmtINR = (val) => `₹${Number(val).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

//--> 144-6: the breakdown lists the calculator's canonical components (brokerage, stt_ctt, exchange_txn, sebi,
//    stamp, gst, clearing, extra fee lines); stored historical results fall back to the legacy keys inside
//    readChargeComponents. Provenance (broker, extrapolation) rides the same breakdown.
const COMPONENT_LABELS = {
    brokerage: 'Brokerage', stt_ctt: 'STT/CTT', exchange_txn: 'Exch Tx', sebi: 'SEBI',
    stamp: 'Stamp', gst: 'GST', clearing: 'Clearing', dp_charges: 'DP charges',
};
const COMPONENT_ORDER = Object.keys(COMPONENT_LABELS);

const CommissionTooltip = ({ breakdown }) => {
    if (!breakdown || typeof breakdown !== 'object') return null;

    const components = readChargeComponents(breakdown);
    const names = [
        ...COMPONENT_ORDER.filter((name) => name in components),
        ...Object.keys(components).filter((name) => !COMPONENT_ORDER.includes(name)).sort(),
    ];
    const items = names.map((name) => ({
        label: COMPONENT_LABELS[name] || name.replaceAll('_', ' '),
        value: components[name],
    }));

    return (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-gray-900 text-white text-xs rounded-lg p-3 whitespace-nowrap shadow-lg z-50 border border-[var(--divider)]">
            {items.map((item, idx) => (
                item.value > 0 && (
                    <div key={idx} className="flex justify-between gap-3">
                        <span>{item.label}:</span>
                        <span className="font-mono">₹{(item.value || 0).toFixed(2)}</span>
                    </div>
                )
            ))}
            <div className="border-t border-[var(--divider)] mt-2 pt-2 font-bold flex justify-between gap-3">
                <span>Total:</span>
                <span className="font-mono">₹{(breakdown.total || 0).toFixed(2)}</span>
            </div>
            {breakdown.broker && (
                <div className="mt-1 text-[10px] text-gray-300">Broker: {breakdown.broker}</div>
            )}
            {breakdown.schedule_extrapolated === true && (
                <div className="text-[10px] text-amber-300">Schedule extrapolated (fill dated before the earliest row)</div>
            )}
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
        </div>
    );
};

/*>>>>>>>>>>>>>>>>>>>>>>  DRILLDOWN GROUPING  <<<<<<<<<<<<<<<<<<<<<<*/

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_NAMES = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// --> Build hierarchical grouping: Year > Quarter > Month > Day > trades[]
const buildDrilldownGroups = (trades, exchangeTimezone) => {
    if (!trades?.length) return [];

    const root = { years: {} };

    for (let i = 0; i < trades.length; i++) {
        const t = trades[i];
        const exitTime = t.exitTime ?? t.exit_time;
        const entryTime = t.entryTime ?? t.entry_time;
        const pnl = Number(t.pnl ?? t.net_pnl ?? 0);
        if (!exitTime) continue;

        const dp = getDatePartsInTimezone(exitTime, exchangeTimezone);
        if (!dp) continue;

        const year = dp.year;
        const monthIdx = dp.month;
        const q = `Q${Math.floor(monthIdx / 3) + 1}`;
        const month = MONTH_NAMES[monthIdx];
        const dayNum = String(dp.day).padStart(2, '0');
        const dayName = DAY_NAMES[dp.weekday];
        const dayKey = `${month} ${dayNum}, ${year} (${dayName})`;
        const isWin = pnl > 0;

        if (!root.years[year]) root.years[year] = { label: String(year), sumPL: 0, wins: 0, total: 0, quarters: {}, start: exitTime, end: exitTime };
        const yNode = root.years[year];

        if (!yNode.quarters[q]) yNode.quarters[q] = { label: `${q} ${year}`, sumPL: 0, wins: 0, total: 0, months: {}, start: exitTime, end: exitTime };
        const qNode = yNode.quarters[q];

        if (!qNode.months[month]) qNode.months[month] = { label: `${month} '${String(year).slice(-2)}`, sumPL: 0, wins: 0, total: 0, days: {}, start: exitTime, end: exitTime };
        const mNode = qNode.months[month];

        if (!mNode.days[dayKey]) mNode.days[dayKey] = { label: dayKey, sumPL: 0, wins: 0, total: 0, trades: [], start: exitTime, end: exitTime };
        const dNode = mNode.days[dayKey];

        const updateNode = (node) => {
            node.sumPL += pnl;
            node.total += 1;
            if (isWin) node.wins += 1;
            if (new Date(entryTime) < new Date(node.start)) node.start = entryTime;
            if (new Date(exitTime) > new Date(node.end)) node.end = exitTime;
        };

        updateNode(yNode);
        updateNode(qNode);
        updateNode(mNode);
        updateNode(dNode);

        // --> Store the original trade with its flat-list index for row numbering
        dNode.trades.push({ ...t, _flatIdx: i });
    }

    // --> Convert to sorted arrays
    return Object.values(root.years).sort((a, b) => b.label - a.label).map(y => ({
        ...y,
        children: Object.values(y.quarters).sort((a, b) => a.label.localeCompare(b.label)).map(q => ({
            ...q,
            children: Object.values(q.months).map(m => ({
                ...m,
                children: Object.values(m.days).sort((a, b) => new Date(b.start) - new Date(a.start)).map(d => d),
            })),
        })),
    }));
};

// --> Collapsible group header row spanning the full table
const GroupHeaderRow = ({ group, depth, colSpan, expanded, onToggle, onSelect }) => {
    const indent = depth * 16;
    const winRate = group.total > 0 ? ((group.wins / group.total) * 100).toFixed(1) : '0';

    return (
        <tr
            className="bg-bg-3/80 dark:bg-bg-1/40 hover:bg-bg-3/80 dark:hover:bg-bg-3/40 cursor-pointer transition-colors border-b border-[var(--divider)]"
            onClick={onToggle}
        >
            <td colSpan={colSpan} className="px-3 py-2">
                <div className="flex items-center" style={{ paddingLeft: `${indent}px` }}>
                    <span className="text-fg-3 w-5 shrink-0">
                        {expanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                    </span>
                    <span className={`text-xs font-bold ${depth === 0 ? 'text-fg-1' : 'text-fg-2'}`}>
                        {group.label}
                    </span>
                    <span className="text-[10px] text-fg-3 bg-bg-3 px-1.5 py-0.5 rounded font-mono ml-2">
                        {group.total} trades
                    </span>
                    <span className="text-[10px] text-fg-3 ml-2">
                        Win: {winRate}%
                    </span>
                    <span className={`text-xs font-bold font-mono ml-auto ${group.sumPL >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {group.sumPL >= 0 ? '+' : ''}{fmtINR(group.sumPL)}
                    </span>
                    {onSelect && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onSelect({ start: group.start, end: group.end }); }}
                            className="ml-2 p-1 text-fg-3 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors"
                            title="View on Chart"
                        >
                            <FiLayers size={12} />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );
};

/*>>>>>>>>>>>>>>>>>>>>>>  MAIN COMPONENT  <<<<<<<<<<<<<<<<<<<<<<*/

const BacktestTradeLog = ({ trades, exchangeTimezone = 'Asia/Kolkata', compact = 'auto', onTradeSelect }) => {
    const data = trades || [];
    const containerRef = useRef(null);
    const [autoCompact, setAutoCompact] = useState(false);
    const [hoveredIdx, setHoveredIdx] = useState(null);
    const [drilldown, setDrilldown] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});

    const isCompact = useMemo(() => {
        if (compact === true) return true;
        if (compact === false) return false;
        return autoCompact;
    }, [compact, autoCompact]);

    // --> Compute drilldown hierarchy
    const drilldownGroups = useMemo(() => {
        if (!drilldown) return [];
        return buildDrilldownGroups(data, exchangeTimezone);
    }, [drilldown, data, exchangeTimezone]);

    // --> Auto-expand top-level groups on first drilldown activation
    useEffect(() => {
        if (drilldown && drilldownGroups.length > 0 && Object.keys(expandedGroups).length === 0) {
            const initial = {};
            drilldownGroups.forEach(y => { initial[`0-${y.label}`] = true; });
            setExpandedGroups(initial);
        }
    }, [drilldown, drilldownGroups]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (compact !== 'auto') return undefined;
        if (typeof ResizeObserver === 'undefined' || !containerRef.current) return undefined;

        const target = containerRef.current;
        const updateCompactState = (width) => {
            setAutoCompact(width < 980);
        };

        updateCompactState(target.clientWidth || 0);

        const resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            updateCompactState(entry.contentRect.width);
        });
        resizeObserver.observe(target);

        return () => {
            resizeObserver.disconnect();
        };
    }, [compact]);

    const toggleGroup = (key) => {
        setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // --> Column count for group header colSpan
    const colCount = useMemo(() => {
        let c = 6; // #, Type, Entry Time, Exit Time, Net PnL, Net %
        if (!isCompact) c += 9; // Lots, Entry Price, Exit Price, MFE, MAE, Gross PnL, Commission, Slippage, Cum P&L
        return c;
    }, [isCompact]);

    // --> Render a single trade row (shared by flat and drilldown modes)
    const renderTradeRow = (trade, displayIdx) => {
        const side = String(trade.side || '').toUpperCase();
        const entryTime = trade.entryTime ?? trade.entry_time;
        const exitTime = trade.exitTime ?? trade.exit_time;
        const entryPrice = asNumber(trade.entryPrice ?? trade.entry_price ?? 0);
        const exitPrice = trade.exitPrice ?? trade.exit_price;
        const pnl = asNumber(trade.pnl ?? trade.net_pnl ?? 0);
        const pnlPctRaw = trade.pnlPct ?? trade.pnl_pct ?? trade.net_pnl_pct;
        const hasPnlPct = pnlPctRaw !== undefined && pnlPctRaw !== null && pnlPctRaw !== '';
        const pnlPct = hasPnlPct ? asNumber(pnlPctRaw, 0) : null;
        const commission = asNumber(trade.commission ?? trade.commission_breakdown?.total ?? trade.fees ?? 0);
        const commissionBreakdown = trade.commission_breakdown ?? {};
        const slippage = asNumber(trade.slippage ?? 0);
        const qtyLots = asNumber(trade.qty_lots ?? trade.qtyLots ?? 1);
        const lotSize = asNumber(trade.lot_size ?? trade.lotSize ?? 1);
        //--> R2-F2: gross is the API-provided field (null on results stored before 144-6) - never derived here.
        const grossRaw = trade.gross_pnl ?? trade.grossPnl;
        const grossPnl = grossRaw === null || grossRaw === undefined || !Number.isFinite(Number(grossRaw)) ? null : Number(grossRaw);
        const mfe = asNumber(trade.mfe ?? 0);
        const mae = asNumber(trade.mae ?? 0);
        const cumulativePnl = asNumber(trade.cumulative_pnl ?? trade.cumulativePnl ?? 0);

        return (
            <tr
                key={displayIdx}
                onClick={() => onTradeSelect?.({ start: entryTime, end: exitTime })}
                className="border-b border-[var(--divider)] hover:bg-blue-50/50 dark:hover:bg-blue-900/20 cursor-pointer transition-colors"
            >
                <td className="px-4 py-3 font-mono text-xs text-fg-2">{displayIdx + 1}</td>
                <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        side === 'LONG'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                        {side || '-'}
                    </span>
                </td>
                {!isCompact && (
                    <td className="px-4 py-3 font-mono text-xs text-fg-2">
                        {qtyLots} × {lotSize}
                    </td>
                )}
                <td className="px-4 py-3 font-mono text-xs text-fg-2">{fmtExTz(entryTime, exchangeTimezone)}</td>
                {!isCompact && <td className="px-4 py-3 text-right font-mono text-xs">₹{entryPrice.toFixed(2)}</td>}
                <td className="px-4 py-3 text-right font-mono text-xs text-fg-2">
                    {fmtExTz(exitTime, exchangeTimezone)}
                </td>
                {!isCompact && (
                    <td className="px-4 py-3 text-right font-mono text-xs">
                        {exitPrice != null ? `₹${Number(exitPrice).toFixed(2)}` : '-'}
                    </td>
                )}
                {!isCompact && (
                    <td className="px-3 py-2 text-right text-xs font-mono text-emerald-500">
                        +{mfe.toFixed(2)}%
                    </td>
                )}
                {!isCompact && (
                    <td className="px-3 py-2 text-right text-xs font-mono text-red-500">
                        -{mae.toFixed(2)}%
                    </td>
                )}
                {!isCompact && (
                    <td className={`px-4 py-3 text-right font-mono text-xs ${grossPnl > 0 ? 'text-emerald-500' : grossPnl < 0 ? 'text-red-500' : 'text-fg-2'}`}>
                        {grossPnl === null ? 'Unresolved' : `${grossPnl > 0 ? '+' : ''}₹${grossPnl.toFixed(2)}`}
                    </td>
                )}
                {!isCompact && (
                    <td className="px-4 py-3 text-right font-mono text-xs text-amber-500 relative"
                        onMouseEnter={() => setHoveredIdx(displayIdx)}
                        onMouseLeave={() => setHoveredIdx(null)}>
                        ₹{commission.toFixed(2)}
                        {hoveredIdx === displayIdx && <CommissionTooltip breakdown={commissionBreakdown} />}
                    </td>
                )}
                {!isCompact && (
                    <td className="px-4 py-3 text-right font-mono text-xs text-fg-2">
                        ₹{slippage.toFixed(2)}
                    </td>
                )}
                <td className={`px-4 py-3 text-right font-mono font-bold text-xs ${
                    pnl > 0 ? 'text-emerald-500' : pnl < 0 ? 'text-red-500' : 'text-fg-2'
                }`}>
                    {pnl > 0 ? '+' : ''}₹{pnl.toFixed(2)}
                </td>
                <td className={`px-4 py-3 text-right font-mono font-bold text-xs ${
                    pnlPct == null ? 'text-fg-2' : (pnlPct > 0 ? 'text-emerald-500' : pnlPct < 0 ? 'text-red-500' : 'text-fg-2')
                }`}>
                    {pnlPct == null ? '-' : `${pnlPct > 0 ? '+' : ''}${pnlPct.toFixed(2)}%`}
                </td>
                {!isCompact && (
                    <td className={`px-3 py-2 text-right text-xs font-mono font-bold ${cumulativePnl >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        ₹{cumulativePnl.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                )}
            </tr>
        );
    };

    // --> Render drilldown groups recursively
    const renderDrilldownRows = (groups, depth = 0) => {
        const rows = [];
        for (const group of groups) {
            const key = `${depth}-${group.label}`;
            const isExpanded = !!expandedGroups[key];

            rows.push(
                <GroupHeaderRow
                    key={`hdr-${key}`}
                    group={group}
                    depth={depth}
                    colSpan={colCount}
                    expanded={isExpanded}
                    onToggle={() => toggleGroup(key)}
                    onSelect={onTradeSelect}
                />
            );

            if (isExpanded) {
                if (group.children) {
                    // --> Intermediate level (Year/Quarter/Month) — recurse
                    rows.push(...renderDrilldownRows(group.children, depth + 1));
                } else if (group.trades) {
                    // --> Day level — render trade table rows
                    for (const trade of group.trades) {
                        rows.push(renderTradeRow(trade, trade._flatIdx));
                    }
                }
            }
        }
        return rows;
    };

    if (data.length === 0) {
        return (
            <div className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] p-8 text-center flex flex-col items-center justify-center h-full">
                <div className="text-fg-3 mb-2">No trades executed yet.</div>
                <div className="text-sm text-fg-2">Run a backtest to see the trade history log.</div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className="bg-bg-2 rounded-xl shadow-sm border border-[var(--divider)] flex flex-col h-full overflow-hidden">
            <div className="p-4 border-b border-[var(--divider)] bg-bg-3/50 dark:bg-bg-1/50 shrink-0 flex items-center justify-between">
                <h3 className="text-sm font-extrabold text-fg-1 uppercase tracking-wider">
                    Trade Log
                </h3>
                {/* Drilldown toggle */}
                <button
                    onClick={() => setDrilldown(prev => !prev)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold transition-all ${
                        drilldown
                            ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400'
                            : 'text-fg-3 hover:text-fg-2 dark:hover:text-fg-3 hover:bg-bg-3'
                    }`}
                    title={drilldown ? 'Switch to flat table' : 'Group by period (Year > Quarter > Month > Day)'}
                >
                    <FiLayers size={13} />
                    Drilldown
                </button>
            </div>

            <div className="flex-1 overflow-auto">
                <table className="w-full text-left text-sm">
                    <thead className="text-[10px] uppercase font-bold text-fg-2 bg-bg-3/80 sticky top-0 z-10 shadow-sm">
                        <tr>
                            <th className="px-4 py-3">#</th>
                            <th className="px-4 py-3">Type</th>
                            {!isCompact && <th className="px-4 py-3">Lots</th>}
                            <th className="px-4 py-3">Entry Time</th>
                            {!isCompact && <th className="px-4 py-3 text-right">Entry Price</th>}
                            <th className="px-4 py-3 text-right">Exit Time</th>
                            {!isCompact && <th className="px-4 py-3 text-right">Exit Price</th>}
                            {!isCompact && <th className="px-3 py-2 text-right text-[10px] font-bold text-fg-3 uppercase">MFE</th>}
                            {!isCompact && <th className="px-3 py-2 text-right text-[10px] font-bold text-fg-3 uppercase">MAE</th>}
                            {!isCompact && <th className="px-4 py-3 text-right">Gross PnL</th>}
                            {!isCompact && <th className="px-4 py-3 text-right">Commission</th>}
                            {!isCompact && <th className="px-4 py-3 text-right">Slippage</th>}
                            <th className="px-4 py-3 text-right">Net PnL</th>
                            <th className="px-4 py-3 text-right">Net %</th>
                            {!isCompact && <th className="px-3 py-2 text-right text-[10px] font-bold text-fg-3 uppercase">Cum. P&L</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {drilldown
                            ? renderDrilldownRows(drilldownGroups)
                            : data.map((trade, idx) => renderTradeRow(trade, idx))
                        }
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BacktestTradeLog;
